import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useOperator } from '@/lib/operator-context';
import { apiFetch } from '@/lib/operator-api';
import {
  Search,
  Filter,
  Download,
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  Bell,
  Trash2,
  Clock,
  Users,
  CalendarDays,
  TrendingUp,
  RefreshCw,
  Plus,
  Loader2,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Separator } from '@/components/ui/separator';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

interface WaitlistEntry {
  id: string;
  businessId: string;
  customerId: string;
  serviceId: string;
  preferredDate: string;
  preferredTimeStart?: string;
  preferredTimeEnd?: string;
  flexibleDates: boolean;
  flexibleDays: number;
  position: number;
  status: 'WAITING' | 'NOTIFIED' | 'BOOKED' | 'EXPIRED' | 'CANCELLED';
  notifiedAt?: string;
  bookedAt?: string;
  expiresAt: string;
  notes?: string;
  createdAt: string;
  customer: { id: string; name: string | null; phoneNumberE164: string; };
  service: { id: string; name: string; durationMinutes: number; priceCents: number; };
  business: { id: string; name: string; };
  staff?: { id: string; name: string; } | null;
}

interface WaitlistStats {
  totalWaiting: number;
  notifiedPending: number;
  bookedFromWaitlist: number;
  expiredToday: number;
  averageWaitDays: number;
  conversionRate: number;
  byService: Array<{ serviceId: string; serviceName: string; count: number }>;
  byDay: Array<{ date: string; count: number }>;
}

type WaitlistStatus = 'ALL' | 'WAITING' | 'NOTIFIED' | 'BOOKED' | 'EXPIRED' | 'CANCELLED';

function getStatusBadgeVariant(status: WaitlistEntry['status']) {
  const map: Record<WaitlistEntry['status'], 'default' | 'secondary' | 'destructive' | 'outline'> = {
    WAITING: 'default', NOTIFIED: 'secondary', BOOKED: 'default', EXPIRED: 'destructive', CANCELLED: 'outline',
  };
  return map[status];
}

function getStatusLabel(status: WaitlistEntry['status'], t: (key: string) => string) {
  const map: Record<WaitlistEntry['status'], string> = {
    WAITING: t('waitlist.waiting'), NOTIFIED: t('waitlist.notified'), BOOKED: t('waitlist.booked'),
    EXPIRED: t('waitlist.expired'), CANCELLED: t('waitlist.cancelled'),
  };
  return map[status];
}

function WaitlistPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { operator, isLoading: authLoading, selectedBusinessId, selectedBusiness } = useOperator();

  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [stats, setStats] = useState<WaitlistStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<WaitlistStatus>('WAITING');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalEntries, setTotalEntries] = useState(0);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [deleteDialogEntry, setDeleteDialogEntry] = useState<WaitlistEntry | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createForm, setCreateForm] = useState({ customerPhone: '', customerName: '', serviceId: '', preferredDate: '', notes: '' });
  const [availableServices, setAvailableServices] = useState<Array<{ id: string; name: string; durationMinutes: number }>>([]);

  // Fetch services when create dialog opens
  useEffect(() => {
    if (!isCreateOpen || !selectedBusinessId) return;
    const controller = new AbortController();
    (async () => {
      try {
        const res = await apiFetch(`/api/operator/services?businessId=${selectedBusinessId}`, { signal: controller.signal });
        if (controller.signal.aborted) return;
        const data = await res.json();
        if (data.success) setAvailableServices(data.data || []);
      } catch (e) {
        if (e instanceof Error && e.name === 'AbortError') return;
        console.error('Failed to fetch services', e);
      }
    })();
    return () => controller.abort();
  }, [isCreateOpen, selectedBusinessId]);

  const handleCreateWaitlist = async () => {
    if (!selectedBusinessId || !createForm.customerPhone || !createForm.serviceId) {
      alert('Phone and service are required');
      return;
    }
    setCreateLoading(true);
    try {
      const phone = createForm.customerPhone.startsWith('+') ? createForm.customerPhone : `+55${createForm.customerPhone}`;
      const response = await apiFetch('/api/operator/waitlist', {
        method: 'POST',
        body: JSON.stringify({
          businessId: selectedBusinessId,
          customerPhone: phone,
          customerName: createForm.customerName || undefined,
          serviceId: createForm.serviceId,
          preferredDate: createForm.preferredDate || undefined,
          notes: createForm.notes || undefined,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setIsCreateOpen(false);
        setCreateForm({ customerPhone: '', customerName: '', serviceId: '', preferredDate: '', notes: '' });
        await fetchWaitlist();
        await fetchStats();
      } else {
        alert(data.error || 'Failed to add to waitlist');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to add to waitlist');
    } finally {
      setCreateLoading(false);
    }
  };

  const fetchWaitlist = useCallback(async (signal?: AbortSignal) => {
    if (!selectedBusinessId) return;
    setLoading(true);
    setFetchError(null);
    try {
      const params = new URLSearchParams({
        businessId: selectedBusinessId, page: String(currentPage), pageSize: String(pageSize),
      });
      if (statusFilter !== 'ALL') params.set('status', statusFilter);
      const response = await apiFetch(`/api/operator/waitlist?${params}`, { signal });
      if (signal?.aborted) return;
      if (!response.ok) throw new Error('Failed to fetch waitlist');
      const data = await response.json();
      setEntries(data.data || []);
      setTotalEntries(data.pagination?.total || 0);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      if (signal?.aborted) return;
      console.error('Failed to fetch waitlist:', error);
      setFetchError(error instanceof Error ? error.message : 'Failed to fetch waitlist');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [selectedBusinessId, currentPage, pageSize, statusFilter, operator?.id]);

  const fetchStats = useCallback(async (signal?: AbortSignal) => {
    if (!selectedBusinessId) return;
    try {
      const response = await apiFetch(
        `/api/operator/waitlist?businessId=${selectedBusinessId}&statsOnly=true`,
        { signal }
      );
      if (signal?.aborted) return;
      if (!response.ok) throw new Error('Failed to fetch stats');
      const data = await response.json();
      setStats(data.data);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error('Failed to fetch waitlist stats:', error);
    }
  }, [selectedBusinessId, operator?.id]);

  useEffect(() => {
    const controller = new AbortController();
    fetchWaitlist(controller.signal);
    fetchStats(controller.signal);
    return () => controller.abort();
  }, [fetchWaitlist, fetchStats]);

  const filteredEntries = useMemo(() => {
    if (!searchQuery) return entries;
    const query = searchQuery.toLowerCase();
    return entries.filter(
      (entry) =>
        entry.customer.name?.toLowerCase().includes(query) ||
        entry.customer.phoneNumberE164.includes(query) ||
        entry.service.name.toLowerCase().includes(query)
    );
  }, [entries, searchQuery]);

  const handleNotify = async (entry: WaitlistEntry) => {
    setActionLoading(entry.id);
    try {
      const response = await apiFetch(`/api/operator/waitlist/${entry.id}/notify`, {
        method: 'POST',
      });
      if (!response.ok) { const error = await response.json(); throw new Error(error.error || 'Failed to notify'); }
      await fetchWaitlist(); await fetchStats();
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to send notification');
    } finally { setActionLoading(null); }
  };

  const handleDelete = async (entry: WaitlistEntry) => {
    setActionLoading(entry.id);
    try {
      const response = await apiFetch(`/api/operator/waitlist/${entry.id}`, {
        method: 'DELETE',
      });
      if (!response.ok) { const error = await response.json(); throw new Error(error.error || 'Failed to remove'); }
      await fetchWaitlist(); await fetchStats();
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to remove from waitlist');
    } finally { setActionLoading(null); setDeleteDialogEntry(null); }
  };

  const handleRefresh = () => { fetchWaitlist(); fetchStats(); };
  const totalPages = Math.ceil(totalEntries / pageSize);

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!operator) return null;

  if (!selectedBusinessId) {
    return (
      <div className="p-6">
        <div className="text-center text-muted-foreground">{t('common.selectBusinessFirst')}</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('waitlist.title')}</h1>
          <p className="text-muted-foreground">
            {selectedBusiness?.name} - {t('waitlist.entryCount', { count: totalEntries })}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setIsCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />{t('waitlist.addToWaitlist')}
          </Button>
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCw className="mr-2 h-4 w-4" />{t('common.refresh')}
          </Button>
          <Button variant="outline">
            <Download className="mr-2 h-4 w-4" />{t('common.export')}
          </Button>
        </div>
      </div>

      {fetchError && (
        <div className="rounded-lg border border-destructive p-4">
          <p className="text-destructive text-sm">{fetchError}</p>
        </div>
      )}

      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('waitlist.totalWaiting')}</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalWaiting}</div>
              <p className="text-xs text-muted-foreground">{stats.notifiedPending} {t('waitlist.pendingResponse')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('waitlist.bookedFromWaitlist')}</CardTitle>
              <CalendarDays className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.bookedFromWaitlist}</div>
              <p className="text-xs text-muted-foreground">{t('waitlist.last30Days')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('waitlist.avgWaitTime')}</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.averageWaitDays} {t('waitlist.days')}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('waitlist.conversionRate')}</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${stats.conversionRate >= 50 ? 'text-green-600' : 'text-yellow-600'}`}>
                {stats.conversionRate}%
              </div>
              <p className="text-xs text-muted-foreground">{t('waitlist.notifiedToBooked')}</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder={t('waitlist.searchPlaceholder')} value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" />
            </div>
            <Select value={statusFilter} onValueChange={(value) => { setStatusFilter(value as WaitlistStatus); setCurrentPage(1); }}>
              <SelectTrigger className="w-[180px]"><SelectValue placeholder={t('waitlist.selectStatus')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">{t('waitlist.allStatuses')}</SelectItem>
                <SelectItem value="WAITING">{t('waitlist.waiting')}</SelectItem>
                <SelectItem value="NOTIFIED">{t('waitlist.notified')}</SelectItem>
                <SelectItem value="BOOKED">{t('waitlist.booked')}</SelectItem>
                <SelectItem value="EXPIRED">{t('waitlist.expired')}</SelectItem>
                <SelectItem value="CANCELLED">{t('waitlist.cancelled')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('waitlist.position')}</TableHead>
                <TableHead>{t('waitlist.customer')}</TableHead>
                <TableHead>{t('waitlist.service')}</TableHead>
                <TableHead>{t('waitlist.preferredDate')}</TableHead>
                <TableHead>{t('waitlist.flexibility')}</TableHead>
                <TableHead>{t('waitlist.status')}</TableHead>
                <TableHead>{t('waitlist.waitingSince')}</TableHead>
                <TableHead className="text-right">{t('waitlist.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEntries.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="h-24 text-center">{t('waitlist.noEntriesFound')}</TableCell></TableRow>
              ) : (
                filteredEntries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="font-medium">#{entry.position}</TableCell>
                    <TableCell>
                      <div>
                        <button className="font-medium text-left hover:text-primary hover:underline transition-colors"
                          onClick={() => navigate({ to: `/operator/customers/${entry.customerId}` })}>
                          {entry.customer.name || t('waitlist.unnamed')}
                        </button>
                        <div className="text-sm text-muted-foreground">{entry.customer.phoneNumberE164}</div>
                      </div>
                    </TableCell>
                    <TableCell>{entry.service.name}</TableCell>
                    <TableCell>
                      <div>
                        <div>{new Date(entry.preferredDate).toLocaleDateString('pt-BR', { weekday: 'short', day: 'numeric', month: 'short' })}</div>
                        {entry.preferredTimeStart && (
                          <div className="text-sm text-muted-foreground">
                            {entry.preferredTimeStart}{entry.preferredTimeEnd && ` - ${entry.preferredTimeEnd}`}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {entry.flexibleDates ? (
                        <Badge variant="outline">+/- {entry.flexibleDays} {t('waitlist.days')}</Badge>
                      ) : (
                        <span className="text-muted-foreground">{t('waitlist.exactDate')}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusBadgeVariant(entry.status)}>{getStatusLabel(entry.status, t)}</Badge>
                      {entry.notifiedAt && (
                        <div className="text-xs text-muted-foreground mt-1">
                          {t('waitlist.notifiedAt')}: {new Date(entry.notifiedAt).toLocaleDateString('pt-BR')}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const days = Math.floor((Date.now() - new Date(entry.createdAt).getTime()) / (1000 * 60 * 60 * 24));
                        if (days === 0) return t('waitlist.today');
                        if (days === 1) return t('waitlist.yesterday');
                        return `${days} ${t('waitlist.daysAgo')}`;
                      })()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {entry.status === 'WAITING' && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:text-blue-700"
                            title={t('waitlist.notifyCustomer')} onClick={() => handleNotify(entry)} disabled={actionLoading === entry.id}>
                            {actionLoading === entry.id ? (
                              <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            ) : (<Bell className="h-4 w-4" />)}
                          </Button>
                        )}
                        {['WAITING', 'NOTIFIED'].includes(entry.status) && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive/80"
                            title={t('waitlist.removeFromWaitlist')} onClick={() => setDeleteDialogEntry(entry)} disabled={actionLoading === entry.id}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-4 border-t">
              <div className="text-sm text-muted-foreground">
                {t('common.showingOf', { from: (currentPage - 1) * pageSize + 1, to: Math.min(currentPage * pageSize, totalEntries), total: totalEntries })}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>
                  {t('common.previous')}
                </Button>
                <span className="text-sm text-muted-foreground">{t('common.pageOf', { current: currentPage, total: totalPages })}</span>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                  {t('common.next')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteDialogEntry} onOpenChange={(open) => !open && setDeleteDialogEntry(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('waitlist.removeConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('waitlist.removeConfirmDescription', {
                customer: deleteDialogEntry?.customer.name || deleteDialogEntry?.customer.phoneNumberE164 || '',
                service: deleteDialogEntry?.service.name || '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteDialogEntry && handleDelete(deleteDialogEntry)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t('waitlist.removeFromWaitlist')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add to Waitlist Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('waitlist.addToWaitlist')}</DialogTitle>
            <DialogDescription>{t('waitlist.addDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('waitlist.phone')} *</Label>
                <Input placeholder="+5511999999999" value={createForm.customerPhone} onChange={(e) => setCreateForm(prev => ({ ...prev, customerPhone: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>{t('waitlist.name')}</Label>
                <Input placeholder={t('waitlist.namePlaceholder')} value={createForm.customerName} onChange={(e) => setCreateForm(prev => ({ ...prev, customerName: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('waitlist.service')} *</Label>
              <Select value={createForm.serviceId} onValueChange={(v) => setCreateForm(prev => ({ ...prev, serviceId: v }))}>
                <SelectTrigger><SelectValue placeholder={t('waitlist.selectService')} /></SelectTrigger>
                <SelectContent>
                  {availableServices.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name} ({s.durationMinutes}min)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('waitlist.preferredDate')}</Label>
              <Input type="date" value={createForm.preferredDate} onChange={(e) => setCreateForm(prev => ({ ...prev, preferredDate: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>{t('waitlist.notes')}</Label>
              <Input placeholder={t('waitlist.notesPlaceholder')} value={createForm.notes} onChange={(e) => setCreateForm(prev => ({ ...prev, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleCreateWaitlist} disabled={createLoading || !createForm.customerPhone || !createForm.serviceId}>
              {createLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('waitlist.add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export const Route = createFileRoute('/_auth/operator/waitlist')({
  component: WaitlistPage,
});
